import angular from "angular"
import { StateParams, StateService } from "angular-ui-router"

import CkRedirect from "../../app/model/domain/ck-redirect"
import type ClassCodeService from "../../app/services/class-code/class-code.service"
import type BreadcrumbService from "../../app/services/breadcrumb-service/breadcrumb.service"
import type AuthService from "../../app/services/auth/auth.service"
import type ToolbarService from "../../app/services/toolbar/toolbar.service"
import type CacheService from "../../app/services/cache/cache.service"
import type RosterService from "../../app/services/roster/roster.service"
import type StorageService from "../../app/services/storage/storage.service"

import { sanitizeClasscode } from "./login/classcode-utils"

interface IClassCodeData {
  allowNewMembers: boolean
  pro: boolean
  hideStudentWork: boolean
  names: string[]
}

export class LoginSources {
  static get LOGIN(): string {
    return "Login"
  }
  static get SIGN_UP(): string {
    return "Sign up"
  }
}

export class LoginPageController {
  public allowNewMembers = false
  public classCode: string
  public classCodePattern = "^[a-zA-Z\\d\\s]+$"
  public hideStudentWork?: boolean
  public isLoading = false
  public name: string
  public showIsteLink = false
  private classCodeIsValid = false
  private classCodeRegex = new RegExp(this.classCodePattern)
  private isCleverClassCode = false
  private isPro?: boolean
  private nameIsValid = false
  private studentNames = new Set<string>()

  constructor(
    private $q: angular.IQService,
    private $log: angular.ILogService,
    private $timeout: angular.ITimeoutService,
    private $state: StateService,
    private $stateParams: StateParams,
    private ClassCodeService: ClassCodeService,
    private BreadcrumbService: BreadcrumbService,
    private AuthService: AuthService,
    private ToolbarService: ToolbarService,
    private CacheService: CacheService,
    private RosterService: RosterService,
    private StorageService: StorageService,
  ) {
    "ngInject"

    this.classCode = $stateParams.classCode
    this.name = $stateParams.name
  }

  public async checkClassCodeValidity(code: string): Promise<{
    isValid: boolean
    hideStudentWork?: boolean
    errorMessage?: string
  }> {
    const cleanedCode = sanitizeClasscode(code)

    if (!cleanedCode) {
      return { isValid: false }
    }
    if (!this.classCodeRegex.test(cleanedCode)) {
      return { isValid: false, errorMessage: "contains invalid characters" }
    }
    if (cleanedCode.length !== 6) {
      return { isValid: false }
    }

    try {
      const classCodeData: IClassCodeData =
        await this.ClassCodeService.getUsernames(cleanedCode)
      if (classCodeData.hideStudentWork) {
        return {
          isValid: true,
          hideStudentWork: true,
          errorMessage:
            "This assignment is hidden. Please ask your teacher to un-hide it so you can continue.",
        }
      }

      this.classCode = cleanedCode
      this.classCodeIsValid = true
      this.allowNewMembers = classCodeData.allowNewMembers
      this.isPro = classCodeData.pro
      this.hideStudentWork = classCodeData.hideStudentWork
      this.studentNames = new Set(
        classCodeData.names.map((name: string) => name.toLowerCase()),
      )
      return {
        isValid: true,
        hideStudentWork: false,
      }
    } catch (err) {
      let errorMessage = "Class code not found."
      if ((err as Error).message === "clever-import created") {
        errorMessage = "This class uses Clever. Please log in with Clever."
      }
      return { isValid: false, errorMessage }
    }
  }

  public checkNameValidity(name: string): string | undefined {
    this.name = name

    if (!name) return undefined

    const nameLower = name.toLowerCase()
    this.nameIsValid = this.studentNames.has(nameLower)

    const isPresent = this.nameIsValid || this.allowNewMembers

    return isPresent
      ? undefined
      : "sorry, you are not on the roster for this assignment. please contact your teacher for assistance."
  }

  get anonStudentLoginButtonMessage(): string {
    return this.canResume ? "Continue" : "Go"
  }

  login(): void {
    if (!this.canStartNew && !this.canResume) {
      return
    }
    this.ToolbarService.reset()
    this.CacheService.reset()
    this.$state.go("root.anon-student-assignment-work", {
      classCode: sanitizeClasscode(this.classCode),
      name: this.name,
      source: this.canStartNew ? LoginSources.SIGN_UP : LoginSources.LOGIN,
      question: 1,
    })
  }

  get canResume(): boolean {
    return this.classCodeIsValid && this.nameIsValid
  }

  get canStartNew(): boolean {
    return this.classCodeIsValid && !!this.name && !this.nameIsValid
  }

  goToAccountLogin(): void {
    if (this.classCodeValid || this.isCleverClassCode) {
      this.StorageService.ckRedirect = new CkRedirect("root.account.home", {
        classCode: sanitizeClasscode(this.classCode),
      })
    }
    this.BreadcrumbService.go("root.account-login", {
      classCode: sanitizeClasscode(this.classCode),
    })
  }

  goToCreateAccountChoice(): void {
    this.BreadcrumbService.go("root.signup-choice")
  }

  get classCodeValid(): boolean {
    return this.classCodeIsValid
  }

  get showPro(): boolean {
    return this.classCodeIsValid && this.isPro === true && !this.hideStudentWork
  }

  get title(): string {
    if (this.showPro) {
      return "Classkick Pro"
    }
    return "Start Working"
  }

  goToCreateStudentAccount(): void {
    const classCode = sanitizeClasscode(this.classCode)
    this.StorageService.ckRedirect = new CkRedirect("root.account.home", {
      classCode,
    })
    this.BreadcrumbService.go("root.signup-student", { classCode })
  }

  goToIsteLoginPage(): void {
    window.location.href = "https://classkick.com/iste"
  }

  goBack(): void {
    this.classCode = ""
    this.classCodeIsValid = false
    this.studentNames.clear()
    this.name = ""
    this.isPro = false
    this.hideStudentWork = false
    this.nameIsValid = false
  }
}
